class UpReveal extends HTMLElement {
  constructor() {
    super();
    this.MAX_UP_PX = 1000;
    this.overlayId =
      `upreveal-overlay-${this.dataset.sectionId || this.closest('[id]')?.id || Math.random().toString(36).slice(2)}`;
  }

  connectedCallback() {
    this.btn = this.querySelector('[data-upreveal-toggle]');
    this.content = this.querySelector('[data-upreveal-content]');
    if (!this.btn || !this.content) return;

    this.startClosed = String(this.dataset.startClosed).toLowerCase() === 'true';
    this.bg = this.dataset.overlayBg || 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.75))';
    this.opPct = parseInt(this.dataset.overlayOpacity || '100', 10);
    this.gap = parseInt(this.dataset.visibleStrip || '100', 10);
    this.style.setProperty('--ur-gap', `${this.gap}px`);

    this.toggle = this.toggle.bind(this);
    this._onScrollResize = this._onScrollResize.bind(this);
    this.openedLabel = this.btn.dataset.buttonLabelOpened;
    this.closedLabel = this.btn.textContent.trim();
    
    if (this.startClosed) this.showOverlay();
    else this.unlock();

    this.btn.addEventListener('click', this.toggle);
  }

  disconnectedCallback() {
    this.hideOverlay();
    if (this.btn) this.btn.removeEventListener('click', this.toggle);
  }

  get overlay() { return document.getElementById(this.overlayId); }

  createOverlay() {
    let ov = this.overlay;
    if (ov) return ov;
    ov = document.createElement('div');
    ov.id = this.overlayId;
    ov.className = 'upreveal__overlay';
    ov.style.setProperty('--ur-bg', this.bg);
    ov.style.setProperty('--ur-op', String(Math.max(0, Math.min(100, this.opPct)) / 100));
    document.body.appendChild(ov);
    return ov;
  }

  positionOverlay() {
    const rect = this.content.getBoundingClientRect();
    const bottom = Math.max(0, Math.round(rect.top) + this.gap);
    const height = Math.min(this.MAX_UP_PX, bottom);
    const top = Math.max(0, bottom - height);

    const ov = this.overlay;
    if (!ov) return;
    ov.style.setProperty('--ur-top', `${top}px`);
    ov.style.setProperty('--ur-height', `${height}px`);
  }

  lock() {
    this.classList.add('is-locked');
    this.btn.setAttribute('aria-expanded', 'true');
  }

  unlock() {
    this.classList.remove('is-locked');
    this.btn.setAttribute('aria-expanded', 'false');
  }

  showOverlay() {
    this.lock();
    const ov = this.createOverlay();
    this.positionOverlay();
    window.addEventListener('scroll', this._onScrollResize, { passive: true });
    window.addEventListener('resize', this._onScrollResize);
    ov.removeAttribute('hidden');
  }

  hideOverlay() {
    const ov = this.overlay;
    if (!ov) { this.unlock(); return; }
    window.removeEventListener('scroll', this._onScrollResize);
    window.removeEventListener('resize', this._onScrollResize);
    this.unlock();
    ov.setAttribute('hidden', '');
    if (ov.parentNode) ov.parentNode.removeChild(ov);
  }

  toggle() {
    if (this.overlay) {
      this.hideOverlay();
      this.btn.textContent = this.openedLabel;
    } else {
      this.showOverlay();
      this.btn.textContent = this.closedLabel;
    }
  }

  _onScrollResize() { this.positionOverlay(); }
}

if (!customElements.get('up-reveal')) {
  customElements.define('up-reveal', UpReveal);
}
